/**
 * widgets/WidgetPanel.tsx — jedna záložka (panel). Povinný chrome (hlavička) je
 * složený ze stejných prvků jako ModuleTopBar (BackButton styl / RefreshButton /
 * gear), ne nová lišta. Grid widgetů přes WidgetShell (jediný renderer).
 *
 * Node resoluce: widget override → default nodu panelu → lokál. Feedback z karet
 * se agreguje v hlavičce a publikuje do UI context registru (čitelné agentem).
 */
import { useCallback, useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, rectSortingStrategy, SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQueryClient } from '@tanstack/react-query';
import { Boxes, GripVertical, Maximize2, Pin, Plus, RotateCcw, Save, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Select } from '../components/ui/Select';
import { setUiContext } from '../lib/ui-context';
import { cn } from '../lib/utils';
import { WidgetShell } from './WidgetShell';
import { ModuleScopeBar } from './ModuleScopeBar';
import { NodePicker } from './NodePicker';
import { WidgetPicker } from './WidgetPicker';
import { REGISTRY_MAP } from './registry';
import { resolveScope } from './scope';
import { useIsPhoneLayout } from '../lib/ui-layout';
import {
  GRID_CLS,
  GRID_CLS_PHONE,
  MIN_H,
  MIN_H_PHONE,
  nextFormat,
  SPAN,
  SPAN_PHONE,
  useViewport,
} from './grid';
import type {
  WidgetFormat,
  WidgetGroup,
  WidgetInstance,
  WidgetPanel as Panel,
  WidgetState,
} from './types';

export function WidgetPanel(
  props: {
    panel: Panel;
    editing?: boolean;
    /** Modulová záložka — zamčený default layout: bez editu/přejmenování/add/drag.
     *  Node picker zůstává (přecílení = fleet control, nemění vzhled). */
    locked?: boolean;
    /** Skryje název v hlavičce (když ho kreslí nadřazený tab bar / ModulePage). */
    showTitle?: boolean;
    /**
     * Modul stránky (ModuleWorkspace) — picker ho nabídne jako první kategorii.
     * Bez prop se odvodí z widgetů na panelu / panel.id.
     */
    preferredModule?: string | null;
    onToggleEdit?: () => void;
    onChange: (p: Panel) => void;
    onSaveDefault?: () => void;
    onResetDefault?: () => void;
  },
) {
  const {
    panel,
    locked,
    showTitle = true,
    preferredModule,
    onToggleEdit,
    onChange,
    onSaveDefault,
    onResetDefault,
  } = props;
  const canEdit = !locked;
  const editing = canEdit && !!props.editing;
  const { t } = useTranslation();
  const viewport = useViewport();
  const phone = useIsPhoneLayout();
  const gridCls = phone ? GRID_CLS_PHONE : GRID_CLS;
  const spanMap = phone ? SPAN_PHONE : SPAN;
  const minH = phone ? MIN_H_PHONE : MIN_H;
  const qc = useQueryClient();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  // Agregace stavů karet — widgetId → stav.
  const [feedback, setFeedback] = useState<Record<string, WidgetState>>({});
  // Phone: karty v horním carouselu (1 aktivní), ne mřížka
  const [phoneCardId, setPhoneCardId] = useState<string | null>(null);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const setWidgets = (widgets: WidgetInstance[]) => onChange({ ...panel, widgets });

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const oldIdx = panel.widgets.findIndex((w) => w.widgetId === active.id);
    const newIdx = panel.widgets.findIndex((w) => w.widgetId === over.id);
    if (oldIdx < 0 || newIdx < 0) return;
    setWidgets(arrayMove(panel.widgets, oldIdx, newIdx));
  };

  const cycleFormat = (id: string) =>
    setWidgets(
      panel.widgets.map((w) =>
        w.widgetId === id
          ? { ...w, format: nextFormat(w.format, REGISTRY_MAP.get(w.widgetId)?.formats) }
          : w
      ),
    );
  const setWidgetNode = (id: string, node: string | null) =>
    setWidgets(panel.widgets.map((w) => (w.widgetId === id ? { ...w, node } : w)));
  const removeWidget = (id: string) =>
    setWidgets(panel.widgets.filter((w) => w.widgetId !== id));
  const addWidget = (widgetId: string) => {
    const def = REGISTRY_MAP.get(widgetId);
    if (!def || panel.widgets.some((w) => w.widgetId === widgetId)) return;
    setWidgets([...panel.widgets, { widgetId, format: def.format }]);
    setPickerOpen(false);
  };

  // ── Spony (skupiny) — mezistupeň scope: widget → spona → záložka → lokál ──────
  // Nod spony má DVĚ úrovně (jako panel node-lock): ŽIVÉ přepnutí (session-only, po
  // reloadu zpět na výchozí spony) + „nastavit jako výchozí" (persist do rozvržení).
  // Živé přepnutí OKAMŽITĚ přecílí VŠECHNY karty ve sponě.
  const groups = panel.groups ?? [];
  const [liveGroupNodes, setLiveGroupNodes] = useState<Record<string, string | null>>({});
  /** Efektivní nod spony: živý override (přednost) → výchozí spony (g.node).
   *  Zachovává undefined = „dědí z panelu". */
  const groupNode = (g: WidgetGroup): string | null | undefined =>
    g.id in liveGroupNodes ? liveGroupNodes[g.id] : g.node;
  const addGroup = () => {
    let n = groups.length + 1;
    while (groups.some((g) => g.id === `g-${n}`)) n++;
    onChange({
      ...panel,
      groups: [...groups, {
        id: `g-${n}`,
        label: t('widgets.groupLabelN', 'Spona {{n}}', { n }),
        node: undefined,
      }],
    });
  };
  /** ŽIVÉ přepnutí cíle spony — session-only (nepersistuje se). */
  const switchGroupNode = (id: string, node: string | null) =>
    setLiveGroupNodes((prev) => ({ ...prev, [id]: node }));
  /** Ulož aktuální (živý) cíl jako VÝCHOZÍ spony (persist) + zahoď override. */
  const pinGroupNode = (id: string) => {
    const node = id in liveGroupNodes
      ? liveGroupNodes[id]
      : (groups.find((g) => g.id === id)?.node ?? null);
    onChange({ ...panel, groups: groups.map((g) => (g.id === id ? { ...g, node: node ?? null } : g)) });
    setLiveGroupNodes((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };
  /** Zahoď živý override → zpět na výchozí spony. */
  const resetGroupNode = (id: string) =>
    setLiveGroupNodes((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  const removeGroup = (id: string) =>
    onChange({
      ...panel,
      groups: groups.filter((g) => g.id !== id),
      widgets: panel.widgets.map((w) => (w.groupId === id ? { ...w, groupId: undefined } : w)),
    });
  const assignGroup = (widgetId: string, groupId: string) =>
    setWidgets(
      panel.widgets.map((w) => (w.widgetId === widgetId ? { ...w, groupId: groupId || undefined } : w)),
    );

  const onFeedback = useCallback((id: string, state: WidgetState) => {
    setFeedback((prev) => (prev[id] === state ? prev : { ...prev, [id]: state }));
  }, []);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await qc.invalidateQueries();
      await qc.refetchQueries({ type: 'active' });
    } finally {
      setTimeout(() => setRefreshing(false), 300);
    }
  };

  // Agregát pro hlavičku + publish do registru (feedback žije na úrovni panelu).
  const errCount = Object.values(feedback).filter((s) => s === 'error').length;
  const phCount = Object.values(feedback).filter((s) => s === 'placeholder').length;
  const inactiveCount = Object.values(feedback).filter((s) => s === 'inactive').length;

  useEffect(() => {
    setUiContext({
      page: 'my-panel',
      tab: panel.label,
      panel_feedback: {
        panel: panel.label,
        node: panel.node ?? 'local',
        widgets: panel.widgets.length,
        errors: errCount,
        placeholders: phCount,
        inactive: inactiveCount,
      },
    });
  }, [panel.label, panel.node, panel.widgets.length, errCount, phCount, inactiveCount]);

  const ungrouped = panel.widgets.filter((w) => !w.groupId);

  // Phone carousel: platný výběr karty
  const phoneCards = phone ? panel.widgets : [];
  const activePhoneId = phoneCardId && phoneCards.some((w) => w.widgetId === phoneCardId)
    ? phoneCardId
    : (phoneCards[0]?.widgetId ?? null);

  // Vykreslí jednu kartu. `sortable` = zapojit do DnD (jen mimo spony — spony
  // jsou mimo DndContext, useSortable by tam spadl). `group` vstupuje do scope.
  const cellFor = (inst: WidgetInstance, group?: typeof groups[number], sortable = false) => {
    const def = REGISTRY_MAP.get(inst.widgetId);
    if (!def) return null;
    const scope = resolveScope(inst, group, panel);
    const shell = (
      <WidgetShell
        def={def}
        scope={scope}
        format={phone ? 'lg' : inst.format}
        viewport={viewport}
        panelNode={group?.node ?? panel.node ?? null}
        editing={editing && !phone}
        onFeedback={(state) => onFeedback(inst.widgetId, state)}
        editActions={
          phone
            ? undefined
            : (
              <div className='flex items-center gap-1'>
                <div className='w-24'>
                  <NodePicker value={inst.node ?? null} onChange={(n) => setWidgetNode(inst.widgetId, n)} />
                </div>
                {groups.length > 0 && (
                  <div className='w-24'>
                    <Select
                      size='sm'
                      value={inst.groupId ?? ''}
                      onChange={(e) => assignGroup(inst.widgetId, e.target.value)}
                      options={[{ value: '', label: t('widgets.noGroup', '— bez spony —') }, ...groups.map((g) => ({ value: g.id, label: g.label }))]}
                    />
                  </div>
                )}
                <button type='button' onClick={() => cycleFormat(inst.widgetId)} className='p-1 text-gray-500 hover:text-gray-200' title={t('widgets.format', 'Formát')}>
                  <Maximize2 size={13} />
                </button>
                <button type='button' onClick={() => removeWidget(inst.widgetId)} className='p-1 text-gray-500 hover:text-red-400' title={t('widgets.remove', 'Odebrat')}>
                  <X size={13} />
                </button>
              </div>
            )
        }
      />
    );
    if (sortable && !phone) {
      return (
        <SortableCell key={inst.widgetId} id={inst.widgetId} format={inst.format} editing={editing}>
          {shell}
        </SortableCell>
      );
    }
    // Jeden lg widget na panelu (= typická modulová stránka) → roztáhni na viewport,
    // ať neplýtváme prázdným místem pod kartou (Filip 2026-07-26 docker feedback).
    const soleLg = !phone && panel.widgets.length === 1 && inst.format === 'lg';
    const heightCls = soleLg ? 'min-h-[calc(100vh-12rem)]' : minH[inst.format];
    return (
      <div key={inst.widgetId} className={cn(spanMap[inst.format], heightCls, 'relative')}>
        {shell}
      </div>
    );
  };

  return (
    <div className='space-y-3'>
      {/* Jednotný chrome: nod + odemčení (sticky, všude stejný) */}
      <div className='space-y-1'>
        {(showTitle || errCount > 0 || phCount > 0 || inactiveCount > 0) && (
          <div className='flex items-center gap-2 flex-wrap min-h-0'>
            {showTitle && (
              <span className='text-lg font-bold text-white'>{panel.label}</span>
            )}
            {errCount > 0 && (
              <Badge variant='red'>{t('widgets.errorCount', '{{count}} chyb', { count: errCount })}</Badge>
            )}
            {phCount > 0 && (
              <Badge variant='yellow'>
                {t('widgets.noPermissionCount', '{{count}} bez práv', { count: phCount })}
              </Badge>
            )}
            {inactiveCount > 0 && (
              <Badge variant='gray'>
                {t('widgets.unavailableCount', '{{count}} nedostupné', { count: inactiveCount })}
              </Badge>
            )}
          </div>
        )}
        <ModuleScopeBar
          node={panel.node ?? null}
          onNodeChange={(node) => onChange({ ...panel, node })}
          editing={editing}
          onToggleEdit={canEdit ? onToggleEdit : undefined}
          onRefresh={handleRefresh}
          refreshing={refreshing}
          sticky
          actions={
            editing
              ? (
                <>
                  <Button
                    variant='ghost'
                    size='sm'
                    icon={<Plus size={14} />}
                    onClick={() => setPickerOpen(true)}
                  >
                    {t('widgets.addWidget', 'Widget')}
                  </Button>
                  <Button
                    variant='ghost'
                    size='sm'
                    icon={<Boxes size={14} />}
                    onClick={addGroup}
                  >
                    {t('widgets.addGroup', 'Spona')}
                  </Button>
                  {onSaveDefault && (
                    <Button
                      variant='ghost'
                      size='sm'
                      icon={<Save size={14} />}
                      onClick={onSaveDefault}
                      title={t('widgets.saveDefault', 'Uložit jako výchozí rozvržení')}
                    >
                      {t('widgets.saveDefaultShort', 'Uložit')}
                    </Button>
                  )}
                  {onResetDefault && (
                    <Button
                      variant='ghost'
                      size='sm'
                      icon={<RotateCcw size={14} />}
                      onClick={onResetDefault}
                      title={t('widgets.loadDefault', 'Obnovit výchozí rozvržení')}
                    >
                      {t('widgets.resetDefaultShort', 'Výchozí')}
                    </Button>
                  )}
                </>
              )
              : undefined
          }
        />
      </div>

      {/* Spony — desktop only (phone: flat carousel všech karet) */}
      {!phone && groups.map((g) => {
        const gw = panel.widgets.filter((w) => w.groupId === g.id);
        const eff = groupNode(g); // string | null | undefined
        const overridden = g.id in liveGroupNodes &&
          (liveGroupNodes[g.id] ?? null) !== (g.node ?? null);
        const effG = { ...g, node: eff };
        return (
          <div key={g.id} className='rounded-2xl border border-gray-800/60 bg-gray-900/20 p-3 space-y-2'>
            <div className='flex items-center gap-2 flex-wrap'>
              <Boxes size={14} className='text-gray-500' />
              <span className='text-sm font-semibold text-gray-300'>{g.label}</span>
              <Badge variant={eff !== undefined ? 'purple' : 'gray'}>
                {eff !== undefined
                  ? (eff ?? t('common.localNode', 'Lokál'))
                  : t('widgets.inherits', 'dědí')}
              </Badge>
              {overridden && <Badge variant='yellow'>{t('widgets.temporary', 'dočasně')}</Badge>}
              {/* ŽIVÝ výběr cíle spony — přecílí VŠECHNY karty spony okamžitě */}
              <div className='ml-auto flex items-center gap-1.5'>
                <span className='text-xs text-gray-500'>{t('widgets.nodeLabel', 'nod:')}</span>
                <div className='w-32'>
                  <NodePicker value={eff ?? null} onChange={(n) => switchGroupNode(g.id, n)} />
                </div>
                {overridden && (
                  <>
                    <button
                      type='button'
                      onClick={() => pinGroupNode(g.id)}
                      className='p-1 text-gray-500 hover:text-purple-300'
                      title={t('widgets.pinGroupNode', 'Nastavit jako výchozí nod spony')}
                    >
                      <Pin size={13} />
                    </button>
                    <button
                      type='button'
                      onClick={() => resetGroupNode(g.id)}
                      className='p-1 text-gray-500 hover:text-gray-200'
                      title={t('widgets.resetGroupNode', 'Zpět na výchozí nod spony')}
                    >
                      <RotateCcw size={13} />
                    </button>
                  </>
                )}
                {editing && (
                  <button
                    type='button'
                    onClick={() => removeGroup(g.id)}
                    className='p-1 text-gray-500 hover:text-red-400'
                    title={t('widgets.removeGroup', 'Zrušit sponu')}
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
            </div>
            {gw.length === 0
              ? (
                <div className='text-xs text-gray-600 py-2'>
                  {t(
                    'widgets.emptyGroup',
                    'Prázdná spona — v edit módu přiřaď karty výběrem „spona" na kartě.',
                  )}
                </div>
              )
              : <div className={gridCls}>{gw.map((inst) => cellFor(inst, effG))}</div>}
          </div>
        );
      })}

      {/* Phone: horní carousel karet (jako Codable swipe) + jedna karta */}
      {phone && phoneCards.length > 0 && (
        <div className='space-y-3'>
          {phoneCards.length > 1 && (
            <>
              <div className='phone-card-carousel'>
                {phoneCards.map((inst) => {
                  const def = REGISTRY_MAP.get(inst.widgetId);
                  const label = def?.title ?? inst.widgetId;
                  const active = inst.widgetId === activePhoneId;
                  return (
                    <button
                      key={inst.widgetId}
                      type='button'
                      className={cn('phone-card-chip', active && 'is-active')}
                      onClick={() => setPhoneCardId(inst.widgetId)}
                    >
                      {def?.icon ? <def.icon size={13} className='shrink-0 opacity-80' /> : null}
                      <span className='truncate'>{label}</span>
                    </button>
                  );
                })}
              </div>
              <div className='phone-swipe-dots'>
                {phoneCards.map((inst) => (
                  <span
                    key={inst.widgetId}
                    className={cn('phone-swipe-dot', inst.widgetId === activePhoneId && 'is-active')}
                  />
                ))}
              </div>
            </>
          )}
          {phoneCards
            .filter((inst) => inst.widgetId === activePhoneId)
            .map((inst) => cellFor(inst, undefined, false))}
        </div>
      )}

      {/* Desktop / edit: mřížka + DnD */}
      {!phone && (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={ungrouped.map((w) => w.widgetId)} strategy={rectSortingStrategy}>
            <div className={gridCls}>
              {ungrouped.map((inst) => cellFor(inst, undefined, true))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {panel.widgets.length === 0 && (
        <div className='text-center text-sm text-gray-600 py-12'>
          {t('ui.emptyPanel', 'Prázdný panel.')}{' '}
          {editing
            ? t('ui.emptyPanelEdit', 'Přidej widget tlačítkem výše.')
            : t('ui.emptyPanelView', 'Klikni na Upravit a přidej widgety.')}
        </div>
      )}

      <WidgetPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        panel={panel}
        preferredModule={preferredModule}
        onPick={addWidget}
      />
    </div>
  );
}

function SortableCell(
  { id, format, editing, children }: {
    id: string;
    format: WidgetFormat;
    editing: boolean;
    children: React.ReactNode;
  },
) {
  const { t } = useTranslation();
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editing,
  });
  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 50 : undefined,
      }}
      className={cn(SPAN[format], MIN_H[format], 'relative')}
    >
      {editing && (
        <button
          type='button'
          {...attributes}
          {...listeners}
          className='absolute -top-2.5 left-2 z-10 p-1 bg-gray-900 border border-gray-700 rounded-lg text-gray-500 hover:text-gray-200 cursor-grab active:cursor-grabbing'
          title={t('ui.move', 'Přesunout')}
        >
          <GripVertical size={13} />
        </button>
      )}
      {children}
    </div>
  );
}
